import { Plus } from "@untitledui/icons";
import type { FC, SVGProps } from "react";
import { DragDotsIcon } from "../components/form-builder/drag-dots-icon";
import type { PaletteFieldType } from "../types";

/**
 * English fallbacks when the remote translation bundle omits a palette key or returns an empty string.
 * Keeps new fields (e.g. textarea) usable in the builder before copy is added server-side.
 */
export const PALETTE_FIELD_DEFAULT_LABELS: Record<PaletteFieldType, string> = {
	text: "Text Input",
	textarea: "Text Area",
	number: "Number",
	email: "Email",
	phone: "Phone",
	date: "Date",
	time: "Time",
	dateTime: "Date & Time",
	select: "Dropdown",
	radio: "Radio Buttons",
	checkbox: "Checkbox",
	file: "File Upload",
	url: "URL",
	multiSelect: "Multi-select",
	users: "User selector",
	signature: "Signature",
	address: "Address",
};

export interface PaletteItemMeta {
	type: PaletteFieldType;
	labelKey: string;
	Icon: FC<SVGProps<SVGSVGElement>>;
}

/** i18n keys under `admin.shared.formEditor.palette.*` */
/**
 * Palette row order follows the Figma frame (60:18166): the first 15 types, then URL and Address.
 * Every row uses the six-dot drag grip.
 */
export const PALETTE_ITEMS: PaletteItemMeta[] = [
	{ type: "text", labelKey: "text", Icon: DragDotsIcon },
	{ type: "textarea", labelKey: "textArea", Icon: DragDotsIcon },
	{ type: "number", labelKey: "number", Icon: DragDotsIcon },
	{ type: "email", labelKey: "email", Icon: DragDotsIcon },
	{ type: "phone", labelKey: "phone", Icon: DragDotsIcon },
	{ type: "date", labelKey: "date", Icon: DragDotsIcon },
	{ type: "time", labelKey: "time", Icon: DragDotsIcon },
	{ type: "dateTime", labelKey: "dateTime", Icon: DragDotsIcon },
	{ type: "select", labelKey: "select", Icon: DragDotsIcon },
	{ type: "radio", labelKey: "radio", Icon: DragDotsIcon },
	{ type: "checkbox", labelKey: "checkbox", Icon: DragDotsIcon },
	{ type: "multiSelect", labelKey: "multiSelect", Icon: DragDotsIcon },
	{ type: "users", labelKey: "users", Icon: DragDotsIcon },
	{ type: "file", labelKey: "file", Icon: DragDotsIcon },
	{ type: "signature", labelKey: "signature", Icon: DragDotsIcon },
	{ type: "url", labelKey: "url", Icon: DragDotsIcon },
	{ type: "address", labelKey: "address", Icon: DragDotsIcon },
];

/**
 * Segment for `admin.shared.formEditor.palette.types.*` from a stored field type.
 * Differs from {@link PaletteFieldType} when the palette id differs from the translation key (rare).
 */
export function paletteFieldTypeToI18nKey(fieldType: PaletteFieldType): string {
	const item = PALETTE_ITEMS.find((p) => p.type === fieldType);
	return item?.labelKey ?? fieldType;
}

export { Plus };
